import React, { memo } from 'react'

import { type VariantProps, cva } from 'class-variance-authority'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import { FaviconAvatar } from '@/components/common/Avatar'
import { CuiOrgIndicator } from '@/components/pursuits/dashboard/ui/CuiOrgIndicator'

import type { Organization } from '@/types/Organization'

const orgDisplayVariants = cva('shrink-0 inline-flex items-center min-w-0', {
  variants: {
    variant: {
      badge:
        'gap-1 rounded-md border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-900 px-1.5 py-0.5 max-w-[140px]',
      menu: 'gap-1.5 max-w-[220px]',
      plain: 'gap-2.5 max-w-full',
      trigger: 'gap-1.5 max-w-[220px]',
    },
    muted: {
      true: 'grayscale opacity-50',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'badge',
    muted: false,
  },
})

const orgDisplayLabelVariants = cva('truncate font-medium', {
  variants: {
    variant: {
      badge: 'text-[11px] text-primary-700 dark:text-primary-200',
      menu: 'text-[13px] text-primary-700 dark:text-primary-200',
      plain: 'text-sm text-primary-800 dark:text-primary-100',
      trigger: 'text-[13px] text-primary-800 dark:text-primary-100',
    },
  },
  defaultVariants: {
    variant: 'badge',
  },
})

type OrgDisplayVariant = NonNullable<
  VariantProps<typeof orgDisplayVariants>['variant']
>

const DEFAULT_AVATAR_SIZE = {
  badge: 16,
  menu: 18,
  plain: 18,
  trigger: 18,
} satisfies Record<OrgDisplayVariant, number>

export interface OrgDisplayProps extends VariantProps<
  typeof orgDisplayVariants
> {
  organization?: Pick<
    Organization,
    'name' | 'website_url' | 'cui_compliance_enabled'
  >
  name?: string
  websiteUrl?: string
  cuiComplianceEnabled?: boolean
  className?: string
  avatarSize?: number
  labelClassName?: string
  labelHref?: string
  labelLinkClassName?: string
  showName?: boolean
}

export const OrgDisplay: React.FC<OrgDisplayProps> = memo(
  ({
    organization,
    name,
    websiteUrl,
    cuiComplianceEnabled,
    variant = 'badge',
    muted,
    className,
    avatarSize,
    labelClassName,
    labelHref,
    labelLinkClassName,
    showName = true,
  }) => {
    const orgName = organization?.name ?? name
    const showCuiIndicator =
      organization?.cui_compliance_enabled ?? cuiComplianceEnabled ?? false
    if (!orgName) return null
    const resolvedVariant = variant ?? 'badge'
    const labelNode = (
      <span
        className={twMerge(
          orgDisplayLabelVariants({ variant: resolvedVariant }),
          labelClassName,
        )}
      >
        {orgName}
      </span>
    )

    return (
      <span
        className={twMerge(
          orgDisplayVariants({ variant: resolvedVariant, muted }),
          className,
        )}
        title={orgName}
        aria-label={orgName}
      >
        <FaviconAvatar
          websiteUrl={organization?.website_url ?? websiteUrl}
          name={orgName}
          width={avatarSize ?? DEFAULT_AVATAR_SIZE[resolvedVariant]}
          height={avatarSize ?? DEFAULT_AVATAR_SIZE[resolvedVariant]}
        />
        {showName &&
          (labelHref ? (
            <Link
              href={labelHref}
              className={twMerge('min-w-0', labelLinkClassName)}
            >
              {labelNode}
            </Link>
          ) : (
            labelNode
          ))}
        {showCuiIndicator && <CuiOrgIndicator className="w-3.5 h-3.5" />}
      </span>
    )
  },
)

OrgDisplay.displayName = 'OrgDisplay'

type OrgBadgeProps = Omit<OrgDisplayProps, 'variant'>

export const OrgBadge: React.FC<OrgBadgeProps> = memo((props) => (
  <OrgDisplay {...props} variant="badge" />
))

OrgBadge.displayName = 'OrgBadge'
